<template>
    <div class="lam-wps-level-container lam-milestone">
        <div
            v-if="showInitials"
            class="lam-wps-row lam-phase-staff"
        >
            <div class="lam-wps-cell column-actions">&nbsp;</div>
            <div class="lam-wps-cell column-title uk-flex-item-1">&nbsp;</div>
            <div class="lam-wps-cell column-trash">&nbsp;</div>
            <div
                v-for="(user, index) in visible_team_members(hide_unstarred_team_members)"
                :key="`${team_capacity_by_index(index)}-${user.id}`"
                class="lam-wps-cell column-lammer"
            >
                <span>{{ user.initials }}</span>
            </div>
            <div class="lam-wps-cell column-total-hours-per-week uk-text-center">&nbsp;</div>
            <div class="lam-wps-cell column-date-range">&nbsp;</div>
            <div class="lam-wps-cell column-duration">&nbsp;</div>
            <div class="lam-wps-cell column-billing">&nbsp;</div>
        </div>
        <div class="lam-wps-row lam-milestone-header">
            <div class="lam-wps-cell column-actions">
                <div
                    v-if="editable"
                    class="lam-drag-handle"
                >
                    <open-icon
                        glyph="arrows-v"
                    />
                </div>
                <base-button
                    v-if="is_editable || !!record.starred"
                    :class="{'uk-active': !!record.starred}"
                    flavor="link"
                    @click.prevent="save_field(!record.starred, 'starred')"
                >
                    <open-icon
                        :glyph="!!record.starred ? 'star' : 'star-o'"
                        :class="{ 'uk-text-warning': !!record.starred }"
                    />
                </base-button>
            </div>
            <div class="lam-wps-cell column-title uk-flex-item-1">
                <div :class="{ disabled: !editable }" class="field-wrapper">
                    <form-checkbox
                        :id="`milestone-${record.id}-completed_at`"
                        name="completed_at"
                        :value="!!record.completed_at"
                        :editable="editable"
                        size="large"
                        :error="has_error('completed_at')"
                        class="field-done"
                        @input="toggle_completed_at"
                    />
                    <form-input
                        :id="`milestone-${record.id}-description`"
                        name="description"
                        v-model="record.description"
                        :editable="is_editable"
                        :required="false"
                        :error="has_error('description')"
                        :input-on-enter="true"
                        class="field-title"
                        @input="save_field"
                    />
                </div>
            </div>
            <div class="lam-wps-cell column-trash">
                <base-button
                    v-if="is_editable"
                    size="small"
                    color="danger"
                    @click.prevent="remove"
                >
                    <open-icon glyph="trash" />
                </base-button>
            </div>
            <div
                v-for="project_team_member in visible_project_team_members(hideUnstarred)"
                :key="project_team_member.id"
                class="lam-wps-cell column-lammer"
            >
                <div class="field-wrapper">
                    <form-input
                        :id="`milestone-${record.id}-project-user-${project_team_member.id}-hours`"
                        type="float"
                        name="hours"
                        :value="proxied_team_member_hours_per_week(record.id, project_team_member.user_id)"
                        :min="0"
                        format="0,0.0"
                        :disabled="!is_editable"
                        :editable="is_editable"
                        :required="false"
                        align="right"
                        :error="has_error('hours')"
                        :input-on-enter="true"
                        class="uk-flex-item-1 lammer-hours"
                        @focus="$event.target.select()"
                        @input="save_team_member_hours_per_week(record.id, project_team_member.user_id, ...arguments)"
                    />
                </div>
            </div>
            <div class="lam-wps-cell column-total-hours-per-week">
                <div class="uk-flex">
                    <strong class="uk-flex-item-1">{{ hours_per_week(hours) | nibnut.number("0,0.0") }}</strong>
                    <label class="uk-flex-item-none">hr/wk</label>
                </div>
            </div>
            <div class="lam-wps-cell column-date-range">
                <div v-if="!is_editable" class="uk-grid uk-grid-small uk-grid-width-1-2">
                    <div>
                        <div class="uk-margin-small-top">
                            {{ record.starts_at | nibnut.date("MM-DD-YYYY") }}&nbsp;
                        </div>
                    </div>
                    <div>
                        <div class="uk-margin-small-top">
                            {{ record.ends_at | nibnut.date("MM-DD-YYYY") }}&nbsp;
                        </div>
                    </div>
                </div>
                <form-date-range-control
                    v-else
                    :id="`milestone-${record.id}-date-range`"
                    name="starts_at"
                    end-name="ends_at"
                    calendar-position="bottom"
                    v-model="record"
                    :editable="true"
                    :required="false"
                    :input-on-enter="true"
                    @change="change_milestone_dates"
                />
            </div>
            <div class="lam-wps-cell column-duration">
                <label>{{ nb_weeks | nibnut.number("0,0[.]0") }} weeks</label>
            </div>
            <div class="lam-wps-cell column-billing">
                <strong>$&nbsp;{{ fee_projected | nibnut.number("0,0") }}</strong>
            </div>
        </div>
        <div class="lam-wps-row lam-milestone-subheader">
            <div class="lam-wps-cell column-actions">&nbsp;</div>
            <div class="lam-wps-cell column-title uk-flex-item-1">&nbsp;</div>
            <div class="lam-wps-cell column-trash">&nbsp;</div>
            <div
                v-for="project_team_member in visible_project_team_members(hideUnstarred)"
                :key="project_team_member.id"
                class="lam-wps-cell column-lammer"
            >
                {{ (proxied_team_member_value(record.id, project_team_member.user_id, 'percent_hours', project_team_member) || 0) | nibnut.number("0") }}%
            </div>
            <div class="lam-wps-cell column-total-hours-per-week">
                <div class="uk-flex">
                    <strong class="uk-flex-item-1">{{ hours }}</strong>
                    <label class="uk-flex-item-none">hrs</label>
                </div>
            </div>
            <div class="lam-wps-cell column-date-range">&nbsp;</div>
            <div class="lam-wps-cell column-duration">&nbsp;</div>
            <div class="lam-wps-cell column-billing">
                {{ percent_projected | nibnut.number("0") }}%
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex"
import { orderBy } from "lodash"

import handles_saving from "../../../mixins/HandlesSaving"
import handles_team_members from "./HandlesTeamMembers"
import task_utilities from "./TaskUtilities"

import FormCheckbox from "../../../ui/FormCheckbox"
import FormInput from "../../../ui/FormInput"
import FormDateRangeControl from "../../../ui/FormDateRangeControl"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"

export default {
    mixins: [handles_saving, handles_team_members, task_utilities],
    components: {
        FormCheckbox,
        FormInput,
        FormDateRangeControl,
        BaseButton,
        OpenIcon
    },
    mounted () {
        this.reroot()
    },
    watch: {
        "record.id": "reroot"
    },
    methods: {
        reroot () {
            this.record_id = this.record ? this.record.id : 0
        },
        toggle_completed_at (completed) {
            this.save_field(completed ? this.$moment().format("YYYY-MM-DD") : null, "completed_at").then(() => [
                this.$emit("toggled")
            ])
        },
        create_task () {
            this.$store.dispatch(
                "CREATE_RECORD",
                {
                    entity: "task",
                    data: {
                        project_id: this.record.project_id,
                        phase_id: this.record.phase_id,
                        milestone_id: this.record.id
                    }
                }
            ).then(() => {
                this.detailed = true
            }).catch(error => {
                this.$error(error.message)
            })
        },
        change_milestone_dates (data) {
            data = this.change_dates(data)
            if(!data.starts_at) data.starts_at = this.$moment(this.record.starts_at).format("YYYY-MM-DD")
            if(!data.ends_at) data.ends_at = this.$moment(this.record.ends_at).format("YYYY-MM-DD")
        },
        total_hours_for_project_team_member (user_id) {
            const tasks = this.showCompleted ? this.tasks : this.visible_tasks
            let total = 0
            tasks.forEach(task => {
                total += this.task_user_value(task.id, user_id, "hours") || 0
            })
            return total
        },
        proxied_team_member_hours_per_week (task_id, user_id) {
            return this.team_member_hours_per_week(task_id, user_id)
        },
        proxied_team_member_value (task_id, user_id, field) {
            return this.task_user_value(task_id, user_id, field)
        },
        sorted (event) {
            const tasks = orderBy(this.tasks, Object.keys(this.manualSort), Object.values(this.manualSort))
            if(event.moved) { // re-order this milestone's tasks
                tasks.splice(event.moved.newIndex, 0, tasks.splice(event.moved.oldIndex, 1)[0])
            } else if(event.added) { // moved task from another milestone to this milestone
                tasks.map(task => task.id)
                tasks.splice(event.added.newIndex, 0, event.added.element)
            }
            if(event.moved || event.added) {
                const ids = []
                tasks.forEach((task, index) => {
                    task.project_id = this.record.project_id
                    task.phase_id = this.record.phase_id
                    task.milestone_id = this.record.id
                    task.sort_order = index
                    ids.push(task.id)
                })
                this.$store.dispatch("RECORD_ACTION", {
                    entity: this.entity,
                    id: this.record.id,
                    action: "sort",
                    data: {
                        ids
                    },
                    method: "post"
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        is_editable () {
            return !!this.record && this.editable && !this.record.completed_at
        },
        hours () {
            return this.record.hours
        },
        fee_projected () {
            return this.record.computed_projected_fee
        },
        percent_projected () {
            if(!this.phaseFeeProjected) return 0
            return (this.fee_projected / this.phaseFeeProjected) * 100
        },
        hide_unstarred_team_members_preference () {
            return this.entity_records("user_preference").find(user_preference => {
                return (user_preference.user_id === this.profile_id) && (user_preference.category === "team-members") && (user_preference.entity === "project") && (user_preference.entity_id === this.record.project_id)
            })
        },
        hide_unstarred_team_members () {
            return !!this.hide_unstarred_team_members_preference
        }
    },
    props: {
        record: {
            type: Object,
            required: true
        },
        manualSort: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        },
        frozen: {
            type: Boolean,
            default: false
        },
        showCompleted: {
            type: Boolean,
            default: false
        },
        hideUnstarred: {
            type: Boolean,
            default: false
        },
        phaseFeeProjected: {
            type: Number,
            default: 0
        },
        showInitials: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            entity: "task",
            detailed: false,
            frozen_row_ids: null
        }
    }
}
</script>

<style lang="less">
.lam-add-on-placeholder {
    position: relative;

    .lam-add-on {
        position: absolute;
        right: 5px;
        top: 50%;
        transform: translate(0, -50%);
        font-size: 0.8em;
    }

    input:focus + .lam-add-on {
        display: none;
    }
}
</style>
